import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

export const DEFAULT_AVATAR_URL =
  'https://img.freepik.com/premium-vector/default-avatar-profile-icon-social-media-user-image-gray-avatar-icon-blank-profile-silhouette-vector-illustration_561158-3467.jpg?semt=ais_hybrid&w=740&q=80';

const ProfileImageContext = createContext();

export const ProfileImageProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id || user?._id || null;

  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) {
      setImageUrl(null);
      return null;
    }
    setLoading(true);
    try {
      const response = await axios.get(`/users/${userId}/profile-image-url`, {
        withCredentials: true
      });
      setImageUrl(response.data?.url || null);
      return response.data?.expiresInSeconds || null;
    } catch (err) {
      if (err.response?.status !== 404) {
        console.warn('Profile image URL fetch failed:', err.message);
      }
      setImageUrl(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial fetch + auto-refresh at 80% of TTL (signed URL expires).
  useEffect(() => {
    let cancelled = false;
    let timer;

    const tick = async () => {
      const expiresIn = await refresh();
      if (cancelled) return;
      if (expiresIn && expiresIn > 0) {
        timer = setTimeout(tick, expiresIn * 800);
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [refresh]);

  return (
    <ProfileImageContext.Provider value={{ imageUrl, loading, refresh }}>
      {children}
    </ProfileImageContext.Provider>
  );
};

export const useProfileImage = () => {
  const ctx = useContext(ProfileImageContext);
  if (!ctx) {
    throw new Error('useProfileImage must be used within a ProfileImageProvider');
  }
  return ctx;
};
